import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Building2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend,
  Area,
  AreaChart
} from 'recharts';
import { TrendingUp, TrendingDown, Activity, AlertTriangle, Heart } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth, eachMonthOfInterval } from 'date-fns';

interface PatientRiskTrendChartProps {
  agencyId?: string;
  agencyName?: string | null;
  startDate?: Date;
  endDate?: Date;
}

interface MonthlyRiskData {
  month: string;
  monthLabel: string;
  totalSessions: number;
  redFlagCases: number;
  redFlagRate: number;
  erCases: number;
  erRate: number;
  avgRiskScore: number;
  highRiskPatients: number;
}

interface TrendInfo {
  direction: 'up' | 'down' | 'stable';
  percentage: number;
  isPositive: boolean;
}

export default function PatientRiskTrendChart({ agencyId, agencyName, startDate: propStartDate, endDate: propEndDate }: PatientRiskTrendChartProps) {
  const chartColors = useChartColors();
  const isAllAgencies = !agencyId;
  const [data, setData] = useState<MonthlyRiskData[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'6' | '12'>('6');
  const [trendInfo, setTrendInfo] = useState<TrendInfo | null>(null);

  useEffect(() => {
    const loadRiskTrendData = async () => {
      setLoading(true);
      try {
        const months = parseInt(timeRange);
        const now = new Date();
        
        // Use prop dates if provided, otherwise use timeRange-based dates
        const startDate = propStartDate || startOfMonth(subMonths(now, months - 1));
        const endDate = propEndDate || endOfMonth(now);

        // Get all months in the range
        const monthsInRange = eachMonthOfInterval({ start: startDate, end: endDate });

        // Fetch triage sessions - filter by agency if provided, otherwise get all
        let query = supabase
          .from('triage_sessions')
          .select('id, patient_id, recommendation, score, red_flags, completed_at')
          .not('completed_at', 'is', null)
          .gte('completed_at', startDate.toISOString())
          .lte('completed_at', endDate.toISOString())
          .order('completed_at', { ascending: true });

        if (agencyId) {
          query = query.eq('agency_id', agencyId);
        }

        const { data: sessions, error } = await query;

        if (error) throw error;

        // Group sessions by month
        const monthlyData: MonthlyRiskData[] = monthsInRange.map(monthDate => {
          const monthStart = startOfMonth(monthDate);
          const monthEnd = endOfMonth(monthDate);
          const monthKey = format(monthDate, 'yyyy-MM');
          const monthLabel = format(monthDate, 'MMM yyyy');

          const monthSessions = (sessions || []).filter(s => {
            const sessionDate = new Date(s.completed_at!);
            return sessionDate >= monthStart && sessionDate <= monthEnd;
          });

          const totalSessions = monthSessions.length;
          const redFlagCases = monthSessions.filter(s => s.red_flags).length;
          const erCases = monthSessions.filter(s => s.recommendation === 'ER_NOW').length;
          
          // Calculate average risk score
          const scoresWithValues = monthSessions.filter(s => s.score !== null);
          const avgRiskScore = scoresWithValues.length > 0
            ? scoresWithValues.reduce((sum, s) => sum + (s.score || 0), 0) / scoresWithValues.length
            : 0;

          // Count unique patients with high risk (score > 70 or red flags)
          const highRiskPatientIds = new Set(
            monthSessions
              .filter(s => s.red_flags || (s.score && s.score > 70))
              .map(s => s.patient_id)
          );

          return {
            month: monthKey,
            monthLabel,
            totalSessions,
            redFlagCases,
            redFlagRate: totalSessions > 0 ? (redFlagCases / totalSessions) * 100 : 0,
            erCases,
            erRate: totalSessions > 0 ? (erCases / totalSessions) * 100 : 0,
            avgRiskScore: Math.round(avgRiskScore),
            highRiskPatients: highRiskPatientIds.size
          };
        });

        setData(monthlyData);

        // Calculate trend (compare first half to second half)
        if (monthlyData.length >= 2) {
          const midpoint = Math.floor(monthlyData.length / 2);
          const firstHalf = monthlyData.slice(0, midpoint);
          const secondHalf = monthlyData.slice(midpoint);

          const avgFirstHalf = firstHalf.reduce((sum, d) => sum + d.redFlagRate, 0) / firstHalf.length;
          const avgSecondHalf = secondHalf.reduce((sum, d) => sum + d.redFlagRate, 0) / secondHalf.length;

          const changePercentage = avgFirstHalf > 0 
            ? ((avgSecondHalf - avgFirstHalf) / avgFirstHalf) * 100 
            : avgSecondHalf > 0 ? 100 : 0;

          setTrendInfo({
            direction: changePercentage > 1 ? 'up' : changePercentage < -1 ? 'down' : 'stable',
            percentage: Math.abs(changePercentage),
            isPositive: changePercentage < 0 // Lower red flag rate is positive
          });
        }
      } catch (error) {
        console.error('Error loading risk trend data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRiskTrendData();
  }, [agencyId, timeRange, propStartDate, propEndDate]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0]?.payload;
      return (
        <div 
          className="rounded-lg shadow-lg p-4 border bg-white"
          style={chartColors.tooltipStyle}
        >
          <p className="font-medium mb-2 text-gray-900">{dataPoint?.monthLabel}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Total Sessions:</span>
              <span className="font-mono text-gray-900">{dataPoint?.totalSessions}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-red-500">Red Flag Cases:</span>
              <span className="font-mono text-red-500">{dataPoint?.redFlagCases} ({dataPoint?.redFlagRate.toFixed(1)}%)</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-amber-500">ER Required:</span>
              <span className="font-mono text-amber-500">{dataPoint?.erCases} ({dataPoint?.erRate.toFixed(1)}%)</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Avg Risk Score:</span>
              <span className="font-mono text-gray-900">{dataPoint?.avgRiskScore}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-purple-500">High Risk Patients:</span>
              <span className="font-mono text-purple-500">{dataPoint?.highRiskPatients}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  const TrendBadge = () => {
    if (!trendInfo) return null;
    
    if (trendInfo.direction === 'stable') {
      return (
        <Badge variant="secondary" className="gap-1">
          <Activity className="h-3 w-3" />
          Stable
        </Badge>
      );
    }

    return (
      <Badge 
        variant="outline" 
        className={`gap-1 ${trendInfo.isPositive 
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 dark:border-emerald-800' 
          : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-800'
        }`}
      >
        {trendInfo.direction === 'up' ? (
          <TrendingUp className="h-3 w-3" />
        ) : (
          <TrendingDown className="h-3 w-3" />
        )}
        {trendInfo.percentage.toFixed(1)}% {trendInfo.direction === 'up' ? 'increase' : 'decrease'}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            Patient Risk Trends
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Calculate summary stats
  const totalRedFlags = data.reduce((sum, d) => sum + d.redFlagCases, 0);
  const totalSessions = data.reduce((sum, d) => sum + d.totalSessions, 0);
  const overallRedFlagRate = totalSessions > 0 ? (totalRedFlags / totalSessions) * 100 : 0;
  const avgMonthlyHighRisk = data.length > 0 
    ? Math.round(data.reduce((sum, d) => sum + d.highRiskPatients, 0) / data.length)
    : 0;

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Heart className="h-5 w-5 text-red-500" />
              Patient Risk Trends
            </CardTitle>
            <CardDescription>
              Track red flag cases and high-risk patient trends over time
            </CardDescription>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="outline" className="gap-1 text-xs font-normal">
              <Building2 className="h-3 w-3" />
              {isAllAgencies ? 'All Agencies' : agencyName || 'Agency'}
            </Badge>
            <TrendBadge />
            <Select value={timeRange} onValueChange={(v) => setTimeRange(v as '6' | '12')}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">Last 6 months</SelectItem>
                <SelectItem value="12">Last 12 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-red-50 dark:bg-red-950/30 rounded-lg p-3 border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-xs font-medium text-red-700 dark:text-red-400">Red Flag Cases</span>
            </div>
            <div className="text-xl font-bold text-red-800 dark:text-red-300">{totalRedFlags}</div>
            <div className="text-xs text-red-600 dark:text-red-500">{overallRedFlagRate.toFixed(1)}% of sessions</div>
          </div>

          <div className="bg-purple-50 dark:bg-purple-950/30 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-700 dark:text-purple-400">Avg High-Risk Patients</span>
            </div>
            <div className="text-xl font-bold text-purple-800 dark:text-purple-300">{avgMonthlyHighRisk}/mo</div>
            <div className="text-xs text-purple-600 dark:text-purple-500">Score &gt;70 or red flags</div>
          </div>

          <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-amber-600" />
              <span className="text-xs font-medium text-amber-700 dark:text-amber-400">ER Required</span>
            </div>
            <div className="text-xl font-bold text-amber-800 dark:text-amber-300">
              {data.reduce((sum, d) => sum + d.erCases, 0)}
            </div>
            <div className="text-xs text-amber-600 dark:text-amber-500">Total ER referrals</div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-400">Total Sessions</span>
            </div>
            <div className="text-xl font-bold text-blue-800 dark:text-blue-300">{totalSessions}</div>
            <div className="text-xs text-blue-600 dark:text-blue-500">In selected period</div>
          </div>
        </div>

        {/* Chart */}
        {data.length > 0 ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="redFlagGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.4} />
                    <stop offset="50%" stopColor="#F87171" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#FCA5A5" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="erGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.4} />
                    <stop offset="50%" stopColor="#FBBF24" stopOpacity={0.2} />
                    <stop offset="100%" stopColor="#FCD34D" stopOpacity={0.05} />
                  </linearGradient>
                  <filter id="riskGlow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                    <feMerge>
                      <feMergeNode in="coloredBlur"/>
                      <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} vertical={false} />
                <XAxis 
                  dataKey="monthLabel" 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: chartColors.axis }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="top" 
                  height={36}
                  iconType="circle"
                  iconSize={8}
                />
                <Area
                  type="monotone"
                  dataKey="redFlagRate"
                  name="Red Flag Rate %"
                  stroke="#EF4444"
                  strokeWidth={3}
                  fill="url(#redFlagGradient)"
                  dot={{ fill: '#EF4444', strokeWidth: 2, stroke: '#fff', r: 4 }}
                  activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: '#EF4444', filter: 'url(#riskGlow)' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Area
                  type="monotone"
                  dataKey="erRate"
                  name="ER Required %"
                  stroke="#F59E0B"
                  strokeWidth={3}
                  fill="url(#erGradient)"
                  dot={{ fill: '#F59E0B', strokeWidth: 2, stroke: '#fff', r: 4 }}
                  activeDot={{ r: 8, strokeWidth: 2, stroke: '#fff', fill: '#F59E0B', filter: 'url(#riskGlow)' }}
                  animationDuration={1500}
                  animationEasing="ease-out"
                  animationBegin={300}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No data available for the selected time period
          </div>
        )}

        {/* Insights */}
        {data.length > 0 && trendInfo && (
          <div className="bg-muted/50 rounded-lg p-4 border border-border">
            <h4 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Trend Analysis
            </h4>
            <p className="text-sm text-muted-foreground">
              {trendInfo.direction === 'stable' ? (
                'Red flag case rates have remained stable over the selected period.'
              ) : trendInfo.isPositive ? (
                `Good news! Red flag rates have decreased by ${trendInfo.percentage.toFixed(1)}% compared to earlier months, indicating improved patient outcomes.`
              ) : (
                `Red flag rates have increased by ${trendInfo.percentage.toFixed(1)}% compared to earlier months. Consider reviewing high-risk patients and care protocols.`
              )}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
